import { useState } from "react";
import { useSnackbar } from "notistack";
import { useFunctionLogs } from "../../hooks/useFunctionLogs";
import { useIcps } from "../../hooks/useIcps";
import { useRescore } from "../../hooks/useMutations";
import { errorPayload, statusFromMutation } from "../../lib/mutationStatus";
import type { RescoreScope } from "../../lib/types";
import { CampaignInput } from "../CampaignInput";
import { ContactResearchResult } from "../results/ContactResearchResult";
import { JsonResult } from "../results/JsonResult";
import { LogStream } from "../LogStream";
import { StatusLine } from "../StatusLine";

export function RescoreCard() {
  const { enqueueSnackbar } = useSnackbar();
  const { data: icps } = useIcps();
  const mutation = useRescore();
  const [runStartTime, setRunStartTime] = useState<Date | null>(null);
  const logs = useFunctionLogs(
    "cf_rescore_lead",
    runStartTime,
    mutation.isPending,
  );

  const [icpName, setIcpName] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [dryRun, setDryRun] = useState(false);
  const [scope, setScope] = useState<RescoreScope>(
    "not_processed_and_not_pushed_elsewhere",
  );
  const [ignoreOtherCampaigns, setIgnoreOtherCampaigns] = useState(false);
  const [ignorePipedriveDedup, setIgnorePipedriveDedup] = useState(true);
  const [limitInput, setLimitInput] = useState("");
  const [offsetInput, setOffsetInput] = useState("");

  const selectedIcp = icpName || icps?.[0]?.name || "";

  const onRun = () => {
    if (!selectedIcp) {
      enqueueSnackbar("ICP name is required.", { variant: "warning" });
      return;
    }
    if (!campaignId.startsWith("cam_")) {
      enqueueSnackbar("Campaign ID must start with cam_.", {
        variant: "warning",
      });
      return;
    }
    const limit = parseOptionalNonNegativeInt(limitInput);
    if (limit === "invalid") {
      enqueueSnackbar("Limit must be a positive integer (≤ 1000).", {
        variant: "warning",
      });
      return;
    }
    if (typeof limit === "number" && (limit < 1 || limit > 1000)) {
      enqueueSnackbar("Limit must be between 1 and 1000.", {
        variant: "warning",
      });
      return;
    }
    const offset = parseOptionalNonNegativeInt(offsetInput);
    if (offset === "invalid") {
      enqueueSnackbar("Offset must be a non-negative integer.", {
        variant: "warning",
      });
      return;
    }
    logs.reset();
    setRunStartTime(new Date());
    mutation.mutate({
      icp_name: selectedIcp,
      campaign_id: campaignId,
      dry_run: dryRun,
      scope,
      ignore_other_campaigns: ignoreOtherCampaigns,
      ignore_pipedrive_dedup: ignorePipedriveDedup,
      ...(typeof limit === "number" ? { limit } : {}),
      ...(typeof offset === "number" ? { offset } : {}),
    });
  };

  // Returns: undefined (empty), a non-negative integer, or "invalid".
  function parseOptionalNonNegativeInt(
    raw: string,
  ): number | undefined | "invalid" {
    const trimmed = raw.trim();
    if (trimmed === "") return undefined;
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < 0) return "invalid";
    return n;
  }

  const { variant, message } = statusFromMutation(mutation);

  return (
    <section className="card">
      <h2>Rescore</h2>
      <p className="card-desc">
        Re-scores previously processed contacts against the selected ICP
        (useful after prompt changes or to evaluate stored people for a new
        ICP) and pushes newly qualified leads to the selected Lemlist
        campaign.
      </p>
      <div className="field">
        <label htmlFor="rs-icp">ICP name</label>
        <select
          id="rs-icp"
          value={selectedIcp}
          onChange={(e) => {
            setIcpName(e.target.value);
            setCampaignId("");
          }}
        >
          {(icps ?? []).map((icp) => (
            <option key={icp.name} value={icp.name}>
              {icp.display_name}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="rs-campaign">Campaign ID</label>
        <CampaignInput
          id="rs-campaign"
          value={campaignId}
          onChange={setCampaignId}
          contextIcp={selectedIcp}
          required
        />
      </div>
      <div className="field">
        <label htmlFor="rs-scope">Scope</label>
        <select
          id="rs-scope"
          value={scope}
          onChange={(e) => setScope(e.target.value as RescoreScope)}
        >
          <option value="not_processed_for_icp">
            Only contacts never scored for this ICP
          </option>
          <option value="not_pushed_to_icp">
            Only contacts scored but not pushed (useful after ICP changes)
          </option>
          <option value="not_processed_and_not_pushed_elsewhere">
            Only fresh leads — never scored for this ICP and not pushed to any other ICP
          </option>
          <option value="not_processed_matches_for_icp">
            Only unprocessed contacts whose Apollo data matches this ICP's region + keyword filters
          </option>
        </select>
      </div>
      <div className="field-row">
        <div className="field">
          <label htmlFor="rs-limit">Limit (max 1000, default 500)</label>
          <input
            type="number"
            id="rs-limit"
            min={1}
            max={1000}
            placeholder="500"
            value={limitInput}
            onChange={(e) => setLimitInput(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="rs-offset">
            Offset (default 0){" "}
            <span
              className="field-help"
              title="Number of matching contacts to skip from the start of the result set. Useful for resuming across runs — e.g. you rescored 400 contacts but only 20 qualified, so set offset to 380 on the next run to skip the contacts you just retried and continue with the next batch."
            >
              ⓘ
            </span>
          </label>
          <input
            type="number"
            id="rs-offset"
            min={0}
            placeholder="0"
            value={offsetInput}
            onChange={(e) => setOffsetInput(e.target.value)}
          />
        </div>
      </div>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={ignoreOtherCampaigns}
          onChange={(e) => setIgnoreOtherCampaigns(e.target.checked)}
        />
        Ignore other Lemlist campaigns (allow contacts to be in multiple
        campaigns at once)
      </label>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={ignorePipedriveDedup}
          onChange={(e) => setIgnorePipedriveDedup(e.target.checked)}
        />
        Ignore Pipedrive dedup (skip filtering out contacts already in
        Pipedrive)
      </label>
      <label className="checkbox-field">
        <input
          type="checkbox"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
        Dry run
      </label>
      <button onClick={onRun} disabled={mutation.isPending}>
        Run
      </button>
      <StatusLine variant={variant} message={message} />
      <LogStream
        entries={logs.entries}
        isPolling={logs.isPolling}
        lastError={logs.lastError}
      />
      {mutation.isSuccess && <ContactResearchResult data={mutation.data} />}
      {mutation.isError && <JsonResult data={errorPayload(mutation.error)} />}
    </section>
  );
}
